/**
 * EcBrindeModal
 *
 * Custom element that manages the full-page brinde (freebie) choice modal.
 *
 * Behaviour:
 *  - Listens for `cart:updated` events dispatched by the theme.
 *  - Fetches /cart.js on every cart change and on initial page load.
 *  - Shows the modal when:
 *      · cart.total_price >= threshold
 *      · none of the configured brinde products are already in the cart
 *      · the user has not dismissed the modal in the current session
 *  - Hides the modal when the above conditions are no longer met.
 *  - Dismissal (X button, overlay click, skip link, Escape key) stores a
 *    flag in sessionStorage — modal will not re-appear for the rest of
 *    the browser session.
 *  - After a product is successfully added, the modal hides automatically.
 *  - Slider: 3 cards visible on desktop (>600 px), 2 on mobile.
 *    Touch-swipe and keyboard arrows are supported.
 */

class EcBrindeModal extends HTMLElement {
  constructor() {
    super();

    // ── Configuration ────────────────────────────────────────────────────
    this.threshold   = parseInt(this.dataset.threshold, 10) || 59900;
    this.productIds  = (this.dataset.productIds || '')
      .split(',')
      .map(id => parseInt(id.trim(), 10))
      .filter(Boolean);

    // ── State ────────────────────────────────────────────────────────────
    this.currentIndex    = 0;
    this.touchStartX     = 0;
    this.isChecking      = false;
    this.hasBeenDismissed = sessionStorage.getItem('brinde-modal-dismissed') === 'true';

    // ── DOM references ───────────────────────────────────────────────────
    this.overlay    = this.querySelector('.brinde-modal__overlay');
    this.container  = this.querySelector('.brinde-modal__container');
    this.closeBtn   = this.querySelector('.brinde-modal__close');
    this.skipBtn    = this.querySelector('.brinde-modal__skip');
    this.prevBtn    = this.querySelector('.brinde-modal__nav--prev');
    this.nextBtn    = this.querySelector('.brinde-modal__nav--next');
    this.track      = this.querySelector('.brinde-modal__track');
    this.trackOuter = this.querySelector('.brinde-modal__track-outer');
    this.cards      = Array.from(this.querySelectorAll('.brinde-modal__card'));
    this.addBtns    = Array.from(this.querySelectorAll('.brinde-modal__add-btn'));

    this._bindEvents();
  }

  connectedCallback() {
    // React to cart changes dispatched by the theme
    document.addEventListener('cart:updated', () => {
      if (this.isChecking) return;
      this.isChecking = true;
      this._checkAndShow().finally(() => {
        setTimeout(() => { this.isChecking = false; }, 600);
      });
    });

    // Initial check after the page settles
    setTimeout(() => this._checkAndShow(), 900);

    // Re-calculate card sizes on resize
    window.addEventListener('resize', this._debounce(() => {
      if (this.hasAttribute('open')) this._initSlider();
    }, 200));
  }

  // ── Event binding ───────────────────────────────────────────────────────

  _bindEvents() {
    // Close button
    this.closeBtn?.addEventListener('click', () => this._dismiss());

    // Skip link
    this.skipBtn?.addEventListener('click', () => this._dismiss());

    // Navigation arrows
    this.prevBtn?.addEventListener('click', () => this._prev());
    this.nextBtn?.addEventListener('click', () => this._next());

    // Add-to-cart buttons
    this.addBtns.forEach(btn => {
      btn.addEventListener('click', () => this._handleAdd(btn));
    });

    // Keyboard: Escape to close, arrows to navigate
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape')     this._dismiss();
      if (e.key === 'ArrowLeft')  this._prev();
      if (e.key === 'ArrowRight') this._next();
    });

    // Touch / swipe on the card track
    this.trackOuter?.addEventListener('touchstart', (e) => {
      this.touchStartX = e.touches[0].clientX;
    }, { passive: true });

    this.trackOuter?.addEventListener('touchend', (e) => {
      const delta = this.touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(delta) > 40) delta > 0 ? this._next() : this._prev();
    }, { passive: true });
  }

  // ── Cart logic ──────────────────────────────────────────────────────────

  async _checkAndShow() {
    try {
      const response = await fetch('/cart.js');
      const cart     = await response.json();

      // Reset dismiss flag when cart is empty so modal can re-trigger
      if (cart.item_count === 0) {
        sessionStorage.removeItem('brinde-modal-dismissed');
        this.hasBeenDismissed = false;
      }

      if (this.hasBeenDismissed) return;

      const brindeInCart = cart.items.some(item =>
        this.productIds.includes(parseInt(item.product_id, 10))
      );

      if (brindeInCart) { this._hide(); return; }

      // Show / hide based on threshold
      cart.total_price >= this.threshold ? this._show() : this._hide();

    } catch (err) {
      console.error('[ec-brinde-modal] Error checking cart:', err);
    }
  }

  // ── Visibility ──────────────────────────────────────────────────────────

  _show() {
    this._resetButtons();
    this.setAttribute('open', '');
    this._initSlider();
    document.body.style.overflow = 'hidden';

    // Move focus inside the modal for accessibility
    requestAnimationFrame(() => {
      (this.closeBtn || this.container)?.focus();
    });
  }

  _resetButtons() {
    this.addBtns.forEach(btn => {
      btn.disabled    = false;
      btn.textContent = 'ADICIONAR';
    });
    this.querySelectorAll('.brinde-modal__variant-select').forEach(sel => {
      sel.classList.remove('is-error');
    });
  }

  _hide() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }

  _dismiss() {
    sessionStorage.setItem('brinde-modal-dismissed', 'true');
    this.hasBeenDismissed = true;
    this._hide();
  }

  // ── Slider ──────────────────────────────────────────────────────────────

  _getVisibleCount() {
    return window.innerWidth <= 600 ? 2 : 3;
  }

  _getCardWidth() {
    const trackW  = this.trackOuter.offsetWidth;
    const visible = this._getVisibleCount();
    const gap     = 16; // matches CSS gap: 16px
    return Math.floor((trackW - gap * (visible - 1)) / visible);
  }

  _initSlider() {
    if (!this.cards.length || !this.trackOuter) return;

    const cardW = this._getCardWidth();

    // Apply explicit pixel width to each card
    this.cards.forEach(card => { card.style.width = `${cardW}px`; });

    // Restore position without animation, then re-enable transition
    this.track.style.transition = 'none';
    this._applyTranslate(this.currentIndex, cardW);
    requestAnimationFrame(() => {
      this.track.style.transition = 'transform 0.3s ease';
    });

    this._updateNavButtons();
  }

  _updateNavButtons() {
    const maxIndex = Math.max(0, this.cards.length - this._getVisibleCount());
    if (this.prevBtn) this.prevBtn.disabled = this.currentIndex <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= maxIndex;
  }

  _prev() {
    if (this.currentIndex <= 0) return;
    this.currentIndex--;
    this._slideTo(this.currentIndex);
  }

  _next() {
    const maxIndex = Math.max(0, this.cards.length - this._getVisibleCount());
    if (this.currentIndex >= maxIndex) return;
    this.currentIndex++;
    this._slideTo(this.currentIndex);
  }

  _slideTo(index) {
    const cardW = this._getCardWidth();
    this._applyTranslate(index, cardW);
    this._updateNavButtons();
  }

  _applyTranslate(index, cardW) {
    const gap    = 16;
    const offset = index * (cardW + gap);
    this.track.style.transform = `translateX(-${offset}px)`;
  }

  // ── Add to cart ─────────────────────────────────────────────────────────

  async _handleAdd(btn) {
    const card      = btn.closest('.brinde-modal__card');
    const select    = card.querySelector('.brinde-modal__variant-select');
    const variantId = select?.value;

    // Validate selection for multi-variant products
    if (!variantId) {
      select.classList.add('is-error');
      select.focus();
      setTimeout(() => select.classList.remove('is-error'), 1600);
      return;
    }

    // Visual feedback
    btn.disabled          = true;
    const originalLabel   = btn.textContent;
    btn.textContent       = 'Adicionando…';

    try {
      const response = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          id:         parseInt(variantId, 10),
          quantity:   1,
          properties: { '_brinde': 'true' },
          // Ask Shopify to return the re-rendered sections so the
          // cart-drawer and cart-counter update without a page reload
          sections:      ['cart-drawer', 'cart-counter'],
          sections_url:  window.location.pathname,
        }),
      });

      if (response.ok) {
        const data = await response.json();

        // Re-render cart-drawer body if present in the response
        this._renderSection(
          data,
          'cart-drawer',
          '#shopify-section-cart-drawer #CartDrawer-Body',
          '#CartDrawer-Body'
        );

        // Re-render cart counter/bubble
        this._renderSection(
          data,
          'cart-counter',
          '#shopify-section-cart-counter',
          '#cart-counter'
        );

        // Dispatch the same event the theme uses so any other listeners react
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        this._hide();
      } else {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }

    } catch (err) {
      console.error('[ec-brinde-modal] Error adding to cart:', err);
      btn.textContent = originalLabel;
      btn.disabled    = false;
    }
  }


  // ── Section rendering ───────────────────────────────────────────────────

  /**
   * Replaces a DOM element using the section HTML returned by Shopify's
   * cart/add.js when the `sections` param is included in the request.
   */
  _renderSection(data, sectionKey, sourceSelector, targetSelector) {
    const sectionHtml = data.sections?.[sectionKey];
    if (!sectionHtml) return;

    const parser = new DOMParser();
    const doc    = parser.parseFromString(sectionHtml, 'text/html');
    const source = doc.querySelector(sourceSelector);
    const target = document.querySelector(targetSelector);

    if (source && target) target.innerHTML = source.innerHTML;
  }

  // ── Utility ─────────────────────────────────────────────────────────────

  _debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

customElements.define('ec-brinde-modal', EcBrindeModal);